import { GetServerSideProps, NextPage } from "next";
import { INextPageProps } from "types";
import { loadServerTranslations } from "../i18n/messages";
import { Locales } from "i18n/country";
import PageModularApi from "@carfax/contentful/page-modular-api";
import { PageModular } from "@carfax/contentful/PageModularModule";
import dynamic from "next/dynamic";
import { ModularPageProps } from "domain/CommonModules/ModularPage";
import { StatusCodes } from "http-status-codes";
import { useLocale, useMessages } from "next-intl";
import { OptimizelyDatafile } from "utils/optimizely/fetchOptimizelyDatafile";
import { fetchOptimizelySsrProps } from "utils/optimizely/optimizely-ssr-props";

const ModularPage = dynamic<ModularPageProps>(() =>
  import("domain/CommonModules/ModularPage").then((mod) => mod.ModularPage),
);

const Error = dynamic(() => import("./_error"), { ssr: false });

export interface IProps extends INextPageProps {
  modularStartPageExists?: boolean;
  modularStartPage?: PageModular;
  optimizelyDatafile?: OptimizelyDatafile | null;
  optimizelyAnonymousId?: string;
}

const Index: NextPage<IProps> = ({ ...props }) => {
  const { modularStartPage, modularStartPageExists } = props;
  const locale = useLocale();
  const messages = useMessages();

  if (modularStartPageExists) {
    return <ModularPage modularPage={modularStartPage} />;
  }
  return <Error statusCode={StatusCodes.NOT_FOUND} locale={locale} messages={messages} />;
};

export const getServerSideProps: GetServerSideProps<IProps> = async (context) => {
  const getModularStartPage = (locale: string): Promise<PageModular> => {
    const homepageSlug = "/";
    return PageModularApi.getInstance().getPageModularBySlug(locale, homepageSlug);
  };

  const locale = Locales.USA;
  const [modularStartPage, messages, optimizelySsrProps] = await Promise.all([
    getModularStartPage(locale),
    loadServerTranslations(locale, [
      "common",
      "page-index",
      "pages-record-check",
      "page-error",
      "page-pricing",
      "packages",
    ]),
    fetchOptimizelySsrProps(context.req),
  ]);

  const modularStartPageExists = Boolean(modularStartPage);

  return {
    props: {
      locale,
      messages,
      modularStartPage,
      modularStartPageExists,
      ...optimizelySsrProps,
    },
  };
};

export default Index;
